// src/features/locker-assignments/components/AssignPhysicalLockers.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
} from '@mui/material';
import { Lock, Layers, Category, Warning, Star } from '@mui/icons-material';
import { useDataProvider, useNotify, useRefresh, useTranslate } from 'react-admin';

interface AssignPhysicalLockersProps {
  open: boolean;
  onClose: () => void;
  assignmentId: string;
  hotelId: string;
}

interface AvailableLocker {
  id: string;
  displayNumber: string;
  typeName: string;
  zoneName: string;
  isRecommended?: boolean;
}

interface AvailableType {
  lockerTypeId: string;
  typeName: string;
  requestedCount: number;
  availableCount: number;
  canFulfill: boolean;
}

interface AvailabilityResponse {
  hasAvailability: boolean;
  status: string;
  message?: string;
  availableTypes?: AvailableType[];
  availableLockers?: AvailableLocker[];
}

const AssignPhysicalLockers: React.FC<AssignPhysicalLockersProps> = ({
  open,
  onClose,
  assignmentId,
  hotelId,
}) => {
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [availability, setAvailability] = useState<AvailabilityResponse | null>(null);
  const [selectedLockers, setSelectedLockers] = useState<string[]>([]);
  const [autoAssign, setAutoAssign] = useState(true);
  
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();

const loadAvailability = useCallback(async () => {
  if (!assignmentId) {
    console.error('ERROR: assignmentId is missing');
    notify('Error: ID de asignación no encontrado', { type: 'error' });
    onClose();
    return;
  }
  
  setLoading(true);
  try {
    const result = await dataProvider.getAvailableLockers(assignmentId, hotelId);
    setAvailability(result.data);
  } catch (error: any) {
    console.error('=== ERROR in loadAvailability ===', error);
    notify(
      translate('resources.locker-assignments.messages.fetch_lockers_error'), 
      { type: 'error' }
    );
    onClose();
  } finally {
    setLoading(false);
  }
}, [dataProvider, assignmentId, hotelId, notify, translate, onClose]);

  useEffect(() => {
 
    
    if (open) {
   
      loadAvailability();
    } else {
   
      setSelectedLockers([]);
      setAutoAssign(true);
      setAvailability(null);
    }
  }, [open, loadAvailability]);



  const handleToggleLocker = (lockerId: string) => {
    setSelectedLockers(prev => {
      if (prev.includes(lockerId)) {
        return prev.filter(id => id !== lockerId);
      } else {
        return [...prev, lockerId];
      }
    });
  };

const handleAssign = async () => {
  setAssigning(true);
  try {
    const lockerIds = autoAssign ? undefined : selectedLockers;
    await dataProvider.assignPhysicalLockers(assignmentId, lockerIds);
    
    notify(
      translate('resources.locker-assignments.messages.assign_success'), 
      { type: 'success' }
    );
    refresh();
    onClose();
  } catch (error: any) {
    console.error('=== ERROR in handleAssign ===', error);
    notify(
      error?.message || translate('resources.locker-assignments.messages.assign_error'),
      { type: 'error' }
    );
  } finally {
    setAssigning(false);
  }
};

  const getTotalRequested = () => {
    if (!availability?.availableTypes) return 0;
    return availability.availableTypes.reduce((sum, type) => sum + type.requestedCount, 0);
  };

  const getTotalAvailable = () => {
    if (!availability?.availableTypes) return 0;
    return availability.availableTypes.reduce((sum, type) => sum + type.availableCount, 0);
  };

  // Agrupar taquillas por tipo para la UI
  const getGroupedLockers = () => {
    if (!availability?.availableLockers) return [];
    
    const grouped = availability.availableLockers.reduce((acc, locker) => {
      if (!acc[locker.typeName]) {
        acc[locker.typeName] = [];
      }
      acc[locker.typeName].push(locker);
      return acc;
    }, {} as Record<string, AvailableLocker[]>);
    
    return Object.entries(grouped).map(([typeName, lockers]) => ({
      typeName,
      lockers,
      availableCount: lockers.length,
    }));
  };

  const hasAvailability = availability?.hasAvailability === true;
  const groupedLockers = getGroupedLockers();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>
        {translate('resources.locker-assignments.dialogs.assign_title')}
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : availability ? (
          <Box>
            {/* Resumen */}
            <Alert 
              severity={hasAvailability ? 'info' : 'warning'} 
              sx={{ mb: 2 }}
            >
              <Typography variant="body2">
                {translate('resources.locker-assignments.messages.requested')}: <strong>{getTotalRequested()}</strong> | 
                {' '}{translate('resources.locker-assignments.messages.available')}: <strong>{getTotalAvailable()}</strong>
              </Typography>
              {availability.message && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {availability.message}
                </Typography>
              )}
            </Alert>

            {/* Mensaje específico cuando no hay disponibilidad */}
            {!hasAvailability && (
              <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>
                <Typography variant="body2">
                  {translate('resources.locker-assignments.messages.no_availability')}
                </Typography>
                {availability.availableTypes && availability.availableTypes.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {availability.availableTypes.map(type => (
                      <Typography key={type.lockerTypeId} variant="caption" display="block">
                        • {type.typeName}: {translate('resources.locker-assignments.messages.need_have', {
                          need: type.requestedCount,
                          have: type.availableCount
                        })}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Alert>
            )}

            {/* Opción de asignación automática - solo si hay disponibilidad */}
            {hasAvailability && getTotalAvailable() > 0 && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={autoAssign}
                      onChange={(e) => setAutoAssign(e.target.checked)}
                    />
                  }
                  label={translate('resources.locker-assignments.assignment.automatic')}
                  sx={{ mb: 2 }}
                />
                
                {/* Lista de taquillas disponibles por tipo */}
                {!autoAssign && groupedLockers.length > 0 && (
                  <Grid container spacing={2}>
                    {groupedLockers.map((group) => (
                      <Grid item xs={12} key={group.typeName}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Category sx={{ mr: 1 }} color="action" />
                              <Typography variant="h6">
                                {group.typeName}
                              </Typography>
                              <Chip
                                label={translate('resources.locker-assignments.messages.available_count', {
                                  count: group.availableCount
                                })}
                                size="small"
                                sx={{ ml: 2 }}
                                color="primary"
                              />
                            </Box>
                            
                            <List dense sx={{ 
                              maxHeight: 300, 
                              overflow: 'auto',
                              '& .MuiListItem-root': {
                                borderRadius: 1,
                                mb: 0.5,
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                },
                              },
                            }}>
                              {group.lockers.map((locker) => (
                                <ListItem key={locker.id}>
                                  <ListItemIcon>
                                    <Checkbox
                                      edge="start"
                                      checked={selectedLockers.includes(locker.id)}
                                      onChange={() => handleToggleLocker(locker.id)}
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography>
                                          {locker.displayNumber}
                                        </Typography>
                                        {locker.isRecommended && (
                                          <Badge
                                            badgeContent={<Star sx={{ fontSize: 12 }} />}
                                            color="warning"
                                            sx={{ 
                                              '& .MuiBadge-badge': { 
                                                right: -8,
                                                top: 8,
                                                backgroundColor: 'transparent',
                                              } 
                                            }}
                                          />
                                        )}
                                      </Box>
                                    }
                                    secondary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Layers fontSize="small" />
                                        <Typography variant="caption">
                                          {locker.zoneName || translate('resources.locker-assignments.fields.no_zone')}
                                        </Typography>
                                        {locker.isRecommended && (
                                          <Chip 
                                            label={translate('resources.locker-assignments.fields.recommended')} 
                                            size="small" 
                                            color="warning"
                                            sx={{ height: 16, fontSize: '0.7rem' }}
                                          />
                                        )}
                                      </Box>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}

            {/* Validación de selección manual */}
            {hasAvailability && !autoAssign && selectedLockers.length === 0 && getTotalAvailable() > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {translate('resources.locker-assignments.messages.select_at_least_one')}
              </Alert>
            )}
          </Box>
        ) : null}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={assigning}>
          {translate('ra.action.cancel')}
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={
            assigning || 
            loading || 
            !hasAvailability ||
            getTotalAvailable() === 0 ||
            (!autoAssign && selectedLockers.length === 0)
          }
          startIcon={assigning ? <CircularProgress size={20} /> : <Lock />}
        >
          {assigning 
            ? translate('resources.locker-assignments.messages.assigning') 
            : translate('resources.locker-assignments.actions.assign')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignPhysicalLockers;